import React, { useState, useRef, useEffect } from 'react';
import { useLanguage, Language, languageNames, languageFlags } from '../contexts/LanguageContext';

interface LanguageSelectorProps {
  style?: React.CSSProperties;
  compact?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ style, compact = false }) => {
  const { language, setLanguage, availableLanguages, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        display: 'inline-block',
        ...style,
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: compact ? '6px 10px' : '8px 14px',
          backgroundColor: 'white',
          border: '2px solid #e1e8ed',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: compact ? '13px' : '14px',
          fontWeight: '500',
          color: '#4a5568',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#667eea';
          e.currentTarget.style.backgroundColor = '#f8f9ff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e1e8ed';
          e.currentTarget.style.backgroundColor = 'white';
        }}
      >
        <span style={{ fontSize: compact ? '16px' : '18px' }}>{languageFlags[language]}</span>
        {!compact && <span>{languageNames[language]}</span>}
        <span style={{ fontSize: '10px', marginLeft: '4px' }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '2px solid #e1e8ed',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 1000,
            minWidth: '150px',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e1e8ed', fontSize: '12px', color: '#718096', fontWeight: '600' }}>
            {t('language.select')}
          </div>
          {availableLanguages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => handleLanguageChange(lang)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                backgroundColor: language === lang ? '#f0f4ff' : 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: language === lang ? '600' : '400',
                color: language === lang ? '#667eea' : '#4a5568',
                textAlign: 'left',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (language !== lang) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = language === lang ? '#f0f4ff' : 'white';
              }}
            >
              <span style={{ fontSize: '18px' }}>{languageFlags[lang]}</span>
              <span>{languageNames[lang]}</span>
              {language === lang && (
                <span style={{ marginLeft: 'auto', color: '#667eea' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
